/**
 * Утилиты для работы с правами доступа
 */

export interface Permission {
  id: number
  name: string
  description: string
  group_id: number
  group_name: string
  dependence: any
  settings: {
    type: 'checkbox' | 'select'
    options?: Array<{ label: string; value: string }>
  }
  priority: number | null
}

export interface PermissionValue {
  id: number
  value: boolean | string | null
}

export interface PermissionTreeNode extends Permission {
  children: PermissionTreeNode[]
}

/**
 * Функция для нормализации зависимости (поддержка разных форматов)
 */
export const normalizeDependence = (dependence: any) => {
  if (!dependence) return null

  // Формат: число (простая зависимость)
  if (typeof dependence === 'number') {
    return { operator: 'AND', conditions: [dependence] }
  }

  // Формат: {conditions: number} (простая зависимость в объекте)
  if (
    typeof dependence === 'object' &&
    dependence.conditions &&
    !Array.isArray(dependence.conditions)
  ) {
    return { operator: 'AND', conditions: [dependence.conditions] }
  }

  // Формат: { operator: 'OR', conditions: [1, 2, 3] } (сложная зависимость)
  if (
    typeof dependence === 'object' &&
    dependence.operator &&
    Array.isArray(dependence.conditions)
  ) {
    return dependence
  }

  return null
}

/**
 * Функция для проверки активности родительского права
 */
export const isParentActive = (
  parentPermission: Permission,
  getPermissionValue: (id: number) => boolean | string | null,
) => {
  const parentValue = getPermissionValue(parentPermission.id)
  return parentPermission.settings.type === 'checkbox'
    ? parentValue === true
    : parentValue !== null && parentValue !== false
}

/**
 * Функция для проверки видимости права с учетом сложных условий
 */
export const isPermissionVisible = (
  permission: Permission,
  permissions: Permission[],
  getPermissionValue: (id: number) => boolean | string | null,
  visited = new Set<number>(),
): boolean => {
  if (visited.has(permission.id)) {
    return false
  }
  visited.add(permission.id)

  // Нормализуем зависимость
  const normalizedDependence = normalizeDependence(permission.dependence)

  // Если у права нет зависимости - оно всегда видимо
  if (!normalizedDependence) {
    return true
  }

  const { operator, conditions } = normalizedDependence

  if (!Array.isArray(conditions)) {
    return false
  }

  // Проверяем условия в зависимости от оператора
  switch (operator) {
    case 'OR':
      // Хотя бы одно условие должно быть выполнено
      return conditions.some((condition: any) => {
        const parentPermission = permissions.find(p => p.id === condition)
        return (
          parentPermission &&
          isParentActive(parentPermission, getPermissionValue) &&
          isPermissionVisible(parentPermission, permissions, getPermissionValue, new Set(visited))
        )
      })

    case 'AND':
      // Все условия должны быть выполнены
      return conditions.every((condition: any) => {
        const parentPermission = permissions.find(p => p.id === condition)
        return (
          parentPermission &&
          isParentActive(parentPermission, getPermissionValue) &&
          isPermissionVisible(parentPermission, permissions, getPermissionValue, new Set(visited))
        )
      })

    default:
      return false
  }
}

/**
 * Функция для построения древовидной структуры прав внутри группы
 */
export const buildPermissionTree = (groupPermissions: Permission[]): PermissionTreeNode[] => {
  const permissionMap = new Map<number, PermissionTreeNode>()

  groupPermissions.forEach(permission => {
    permissionMap.set(permission.id, {
      ...permission,
      children: [],
    })
  })

  const tree: PermissionTreeNode[] = []

  groupPermissions.forEach(permission => {
    // Нормализуем зависимость для проверки
    const normalizedDependence = normalizeDependence(permission.dependence)

    // Для простых зависимостей
    if (normalizedDependence && normalizedDependence.conditions.length === 1) {
      const parentId = normalizedDependence.conditions[0]
      const parent = permissionMap.get(parentId)
      const current = permissionMap.get(permission.id)
      if (parent && current) {
        parent.children.push(current)
      } else if (current) {
        tree.push(current)
      }
    } else {
      // Для сложных зависимостей или прав без зависимостей - добавляем в корень
      const current = permissionMap.get(permission.id)
      if (current) {
        tree.push(current)
      }
    }
  })

  // Сортируем дерево по приоритету
  const sortTree = (nodes: PermissionTreeNode[]): PermissionTreeNode[] => {
    return nodes
      .sort((a, b) => {
        const priorityA = a.priority !== null ? a.priority : Infinity
        const priorityB = b.priority !== null ? b.priority : Infinity
        return priorityA - priorityB
      })
      .map(node => {
        if (node.children.length > 0) {
          node.children = sortTree(node.children)
        }
        return node
      })
  }

  return sortTree(tree)
}

/**
 * Функция для проверки, есть ли в группе видимые права
 */
export const hasVisiblePermissions = (
  permissionTree: PermissionTreeNode[],
  permissions: Permission[],
  getPermissionValue: (id: number) => boolean | string | null,
): boolean => {
  for (const node of permissionTree) {
    if (isPermissionVisible(node, permissions, getPermissionValue)) {
      return true
    }
    if (node.children && node.children.length > 0) {
      if (hasVisiblePermissions(node.children, permissions, getPermissionValue)) {
        return true
      }
    }
  }
  return false
}

/**
 * Функция для группировки прав по группам с сохранением порядка групп
 */
export const getGroupedPermissions = (permissions: Permission[]) => {
  const groupsMap = new Map<
    number,
    {
      groupId: number
      groupName: string
      permissions: Permission[]
    }
  >()

  permissions.forEach(permission => {
    const groupId = permission.group_id
    const groupName = permission.group_name
    if (!groupsMap.has(groupId)) {
      groupsMap.set(groupId, {
        groupId,
        groupName,
        permissions: [],
      })
    }
    groupsMap.get(groupId)!.permissions.push(permission)
  })

  return Array.from(groupsMap.values()).sort((a, b) => a.groupId - b.groupId)
}
